// src/pages/TraineesPage.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    collection,
    query,
    orderBy,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp,
    Timestamp,
    writeBatch
} from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiUsers, FiX } from 'react-icons/fi';
import { db, auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/styleUtils';
import styles from '../styles/TraineesPage.module.css';

// Define roles that can access and manage this page
const traineePageAccessAndManagementRoles = ['Manager', 'Accounts Manager'];
const DEFAULT_TRAINEE_ROLE = 'Trainee'; // This is used to filter out trainees from supervisor list

const TraineesPage = () => {
    const { currentUser, userRole } = useAuth();

    const [trainees, setTrainees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const [showTraineeModal, setShowTraineeModal] = useState(false);
    const [editingTrainee, setEditingTrainee] = useState(null);
    const [selectedSupervisorIds, setSelectedSupervisorIds] = useState([]);

    const [allUsers, setAllUsers] = useState([]);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');

    const isMounted = useRef(true);
    const successTimeoutRef = useRef(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
            if (successTimeoutRef.current) {
                clearTimeout(successTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (successMessage) {
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
            successTimeoutRef.current = setTimeout(() => {
                if (isMounted.current) setSuccessMessage('');
            }, 4000);
        }
        return () => {
            if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        };
    }, [successMessage]);

    useEffect(() => {
        if (!currentUser || !traineePageAccessAndManagementRoles.includes(userRole)) {
            setAllUsers([]);
            return;
        }
        const usersCollectionRef = collection(db, 'users');
        const qUsers = query(usersCollectionRef, orderBy('name', 'asc'));
        const unsubscribeUsers = onSnapshot(qUsers, (snapshot) => {
            if (!isMounted.current) return;
            const userList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setAllUsers(userList);
        }, (err) => {
            console.error("Error fetching all users:", err);
            if(isMounted.current) setError(prev => (prev ? prev + " | " : "") + "Failed to load users for supervisor selection.");
        });
        return () => unsubscribeUsers();
    }, [currentUser, userRole]);

    useEffect(() => {
        if (!currentUser) {
            if (isMounted.current) { setLoading(false); setTrainees([]); setError("Please log in to view trainees."); }
            return;
        }
        if (!userRole || !traineePageAccessAndManagementRoles.includes(userRole)) {
            if (isMounted.current) { setError("You do not have permission to view this page."); setLoading(false); setTrainees([]); }
            return;
        }

        setLoading(true); setError('');
        const traineesCollectionRef = collection(db, 'trainees');
        const q = query(traineesCollectionRef, orderBy('name', 'asc'));

        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            if (!isMounted.current) return;
            const fetchedTrainees = querySnapshot.docs.map(doc => ({
                id: doc.id, ...doc.data(),
                startDate: doc.data().startDate?.toDate(),
                supervisorIds: Array.isArray(doc.data().supervisorIds) ? doc.data().supervisorIds : [],
                supervisorNames: Array.isArray(doc.data().supervisorNames) ? doc.data().supervisorNames : [],
            }));
            setTrainees(fetchedTrainees); setLoading(false);
        }, (err) => {
            if (!isMounted.current) return;
            console.error("Error fetching trainees:", err);
            setError("Failed to load trainees. Please try refreshing."); setLoading(false);
        });
        return () => unsubscribe();
    }, [currentUser, userRole]);

    const potentialSupervisors = useMemo(() => {
        // MODIFIED: Filter out users with the DEFAULT_TRAINEE_ROLE
        return allUsers.filter(user => user.role !== DEFAULT_TRAINEE_ROLE);
    }, [allUsers]);

    const stats = useMemo(() => {
        const s = { total: trainees.length, active: 0, completed: 0, dropped: 0 };
        trainees.forEach((t) => {
            const st = t.status || 'Active';
            if (st === 'Active') s.active += 1;
            else if (st === 'Completed') s.completed += 1;
            else if (st === 'Dropped') s.dropped += 1;
        });
        return s;
    }, [trainees]);

    const filteredTrainees = useMemo(() => {
        const term = search.trim().toLowerCase();
        return trainees.filter((t) => {
            if (statusFilter !== 'all' && (t.status || 'Active') !== statusFilter) return false;
            if (term) {
                const hay = `${t.name || ''} ${t.email || ''}`.toLowerCase();
                if (!hay.includes(term)) return false;
            }
            return true;
        });
    }, [trainees, search, statusFilter]);

    const statusClass = (status) => {
        const st = status || 'Active';
        if (st === 'Completed') return styles.badgeCompleted;
        if (st === 'Dropped') return styles.badgeDropped;
        return styles.badgeActive;
    };

    const handleOpenAddModal = () => {
        if (!traineePageAccessAndManagementRoles.includes(userRole)) { setError("You do not have permission to add trainees."); return; }
        setEditingTrainee(null);
        setSelectedSupervisorIds([]);
        setShowTraineeModal(true);
        setError(''); setSuccessMessage('');
    };

    const handleOpenEditModal = (trainee) => {
        if (!traineePageAccessAndManagementRoles.includes(userRole)) { setError("You do not have permission to edit trainees."); return; }
        setEditingTrainee(trainee);
        setSelectedSupervisorIds(trainee.supervisorIds || []);
        setShowTraineeModal(true);
        setError(''); setSuccessMessage('');
    };

    const handleCloseModal = () => {
        setShowTraineeModal(false);
        setEditingTrainee(null);
        setSelectedSupervisorIds([]);
        setError('');
    };

    const handleSupervisorSelectionChange = (supervisorId) => {
        setSelectedSupervisorIds(prevSelectedIds =>
            prevSelectedIds.includes(supervisorId)
                ? prevSelectedIds.filter(id => id !== supervisorId)
                : [...prevSelectedIds, supervisorId]
        );
    };

    const handleSaveTrainee = async (formData, password) => {
        if (!currentUser) { setError("Authentication error."); return; }
        if (!traineePageAccessAndManagementRoles.includes(userRole)) { setError("You do not have permission to save trainee data."); return; }

        setIsProcessing(true); setError(''); setSuccessMessage('');

        const selectedSupervisorNames = selectedSupervisorIds
            .map(id => {
                const supervisorUser = allUsers.find(user => user.id === id);
                return supervisorUser ? supervisorUser.name : null;
            })
            .filter(name => name !== null);

        const traineePayload = {
            name: formData.name,
            email: formData.email,
            startDate: formData.startDate ? Timestamp.fromDate(new Date(formData.startDate)) : null,
            status: formData.status,
            supervisorIds: selectedSupervisorIds,
            supervisorNames: selectedSupervisorNames,
        };

        try {
            if (editingTrainee && editingTrainee.id) {
                const traineeRef = doc(db, 'trainees', editingTrainee.id);
                const updateData = {
                    ...traineePayload,
                    updatedAt: serverTimestamp(),
                    updatedBy: currentUser.uid,
                };
                await updateDoc(traineeRef, updateData);
                setSuccessMessage(`Trainee "${formData.name}" updated successfully.`);

                if (formData.name !== editingTrainee.name) {
                    if (editingTrainee.authUid) {
                        const userProfileRef = doc(db, 'users', editingTrainee.authUid);
                        await updateDoc(userProfileRef, { name: formData.name });
                        console.log("User profile name updated for:", editingTrainee.authUid);
                    }
                }
            } else {
                if (!password) {
                    setError("Password is required for new trainees.");
                    setIsProcessing(false);
                    return;
                }
                let newUserCredential;
                try {
                    newUserCredential = await createUserWithEmailAndPassword(auth, formData.email, password);
                } catch (authError) {
                    console.error("Error creating Firebase Auth user:", authError);
                    let specificError = "Failed to create user account: " + authError.message;
                    if (authError.code === 'auth/email-already-in-use') specificError = "This email is already registered.";
                    else if (authError.code === 'auth/weak-password') specificError = "Password is too weak (min 6 characters).";
                    setError(specificError);
                    setIsProcessing(false);
                    return;
                }
                const newAuthUser = newUserCredential.user;

                const batch = writeBatch(db);
                const newTraineeRef = doc(collection(db, 'trainees'));
                batch.set(newTraineeRef, {
                    ...traineePayload,
                    authUid: newAuthUser.uid,
                    createdAt: serverTimestamp(),
                    createdBy: currentUser.uid,
                });

                const userProfileRef = doc(db, 'users', newAuthUser.uid);
                batch.set(userProfileRef, {
                    uid: newAuthUser.uid, name: formData.name, email: formData.email,
                    role: DEFAULT_TRAINEE_ROLE, createdAt: serverTimestamp(), photoURL: null,
                });

                await batch.commit();
                setSuccessMessage(`Trainee "${formData.name}" and their account created successfully.`);
            }
            handleCloseModal();
        } catch (err) {
            console.error("Error saving trainee:", err);
            setError("Failed to save trainee data. Please try again. " + err.message);
        } finally {
            if (isMounted.current) setIsProcessing(false);
        }
    };

    const handleDeleteTrainee = async (traineeId, traineeName) => {
        if (!currentUser || !traineePageAccessAndManagementRoles.includes(userRole)) {
            setError("You do not have permission to delete trainees."); return;
        }
        if (!window.confirm(`Are you sure you want to delete trainee "${traineeName || 'this trainee'}"? This may also delete their login account if linked. This action cannot be undone.`)) {
            return;
        }

        setIsProcessing(true); setError(''); setSuccessMessage('');
        const traineeToDelete = trainees.find(t => t.id === traineeId);

        try {
            const traineeRef = doc(db, 'trainees', traineeId);
            await deleteDoc(traineeRef);

            if (traineeToDelete?.authUid) {
                const userProfileRef = doc(db, 'users', traineeToDelete.authUid);
                try {
                    await deleteDoc(userProfileRef);
                    console.log(`User profile ${traineeToDelete.authUid} deleted from 'users'.`);
                } catch (userProfileError) {
                    console.warn(`Could not delete user profile ${traineeToDelete.authUid}:`, userProfileError);
                    setError(prev => (prev ? prev + " | " : "") + `Warning: User profile in 'users' might still exist.`);
                }
                console.warn(`Firebase Auth user deletion for UID ${traineeToDelete.authUid} typically requires Admin SDK (e.g., Cloud Function) for security. Manual deletion might be needed.`);
                setSuccessMessage(`Trainee "${traineeName}" data deleted. Associated user profile deleted (if found). Auth account may require admin deletion.`);
            } else {
                setSuccessMessage(`Trainee "${traineeName}" data deleted.`);
            }
        } catch (err) {
            console.error("Error deleting trainee:", err);
            setError("Failed to delete trainee. " + err.message);
        } finally {
            if (isMounted.current) setIsProcessing(false);
        }
    };

    const canManageTrainees = traineePageAccessAndManagementRoles.includes(userRole);

    if (loading && !trainees.length && !error) {
        return <div className={styles.loadingIndicator}>Loading trainees...</div>;
    }
    const fullPageErrorCond = (error && trainees.length === 0 && (error.startsWith("You do not have permission") || error.startsWith("Please log in")));
    if (fullPageErrorCond) {
        return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error}</div>;
    }

    return (
        <div className={styles.pageContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.title}>Trainees</h1>
                    <p className={styles.subtitle}>Manage trainee accounts, supervisors and progress status.</p>
                </div>
                {canManageTrainees && (
                    <button onClick={handleOpenAddModal} className={styles.addButton} disabled={isProcessing}>
                        <FiPlus /> Add Trainee
                    </button>
                )}
            </div>

            {error && !showTraineeModal && !fullPageErrorCond && <div className={styles.errorMessage}>{error}</div>}
            {successMessage && <div className={styles.successMessage}>{successMessage}</div>}

            {/* Stats */}
            <div className={styles.statsRow}>
                <div className={`${styles.statCard} ${styles.statTotal}`}>
                    <div className={styles.statLabel}>Total Trainees</div>
                    <div className={styles.statValue}>{stats.total}</div>
                </div>
                <div className={`${styles.statCard} ${styles.statActive}`}>
                    <div className={styles.statLabel}>Active</div>
                    <div className={styles.statValue}>{stats.active}</div>
                </div>
                <div className={`${styles.statCard} ${styles.statCompleted}`}>
                    <div className={styles.statLabel}>Completed</div>
                    <div className={styles.statValue}>{stats.completed}</div>
                </div>
                <div className={`${styles.statCard} ${styles.statDropped}`}>
                    <div className={styles.statLabel}>Dropped</div>
                    <div className={styles.statValue}>{stats.dropped}</div>
                </div>
            </div>

            {/* Toolbar */}
            <div className={styles.toolbar}>
                <div className={styles.searchBox}>
                    <FiSearch className={styles.searchIcon} />
                    <input
                        type="text"
                        placeholder="Search by name or email…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className={styles.searchInput}
                    />
                </div>
                <select
                    className={styles.filterSelect}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <option value="all">All statuses</option>
                    <option value="Active">Active</option>
                    <option value="Completed">Completed</option>
                    <option value="Dropped">Dropped</option>
                </select>
            </div>

            {trainees.length === 0 && !loading && !error ? (
                <div className={styles.emptyState}>
                    <FiUsers className={styles.emptyIcon} />
                    <p>No trainees yet.</p>
                    {canManageTrainees && <span>Click “Add Trainee” to create the first one.</span>}
                </div>
            ) : filteredTrainees.length === 0 ? (
                <div className={styles.emptyState}>
                    <FiSearch className={styles.emptyIcon} />
                    <p>No trainees match your filters.</p>
                </div>
            ) : (
                <div className={styles.tableWrap}>
                    <table className={styles.traineesTable}>
                        <thead>
                            <tr>
                                <th>Trainee</th>
                                <th>Start Date</th>
                                <th>Status</th>
                                <th>Supervisors</th>
                                {canManageTrainees && <th className={styles.actionsHead}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTrainees.map(trainee => (
                                <tr key={trainee.id}>
                                    <td data-label="Trainee">
                                        <div className={styles.personCell}>
                                            <div className={styles.avatar}>
                                                {(trainee.name || trainee.email || '?').charAt(0).toUpperCase()}
                                            </div>
                                            <div className={styles.personText}>
                                                <div className={styles.personName}>{trainee.name || 'N/A'}</div>
                                                <div className={styles.personEmail}>{trainee.email || 'N/A'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td data-label="Start Date">{trainee.startDate ? formatDate(trainee.startDate) : '—'}</td>
                                    <td data-label="Status">
                                        <span className={`${styles.badge} ${statusClass(trainee.status)}`}>
                                            {trainee.status || 'Active'}
                                        </span>
                                    </td>
                                    <td data-label="Supervisors">
                                        {trainee.supervisorNames && trainee.supervisorNames.length > 0 ? (
                                            <div className={styles.supChips}>
                                                {trainee.supervisorNames.map((n, i) => (
                                                    <span key={i} className={styles.supChip}>{n}</span>
                                                ))}
                                            </div>
                                        ) : <span className={styles.muted}>—</span>}
                                    </td>
                                    {canManageTrainees && (
                                        <td data-label="Actions" className={styles.actionsCell}>
                                            <button onClick={() => handleOpenEditModal(trainee)} className={`${styles.actionButton} ${styles.editButton}`} disabled={isProcessing} title="Edit">
                                                <FiEdit2 />
                                            </button>
                                            <button onClick={() => handleDeleteTrainee(trainee.id, trainee.name)} className={`${styles.actionButton} ${styles.deleteButton}`} disabled={isProcessing} title="Delete">
                                                <FiTrash2 />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {showTraineeModal && (
                <div className={styles.modalBackdrop} onClick={handleCloseModal}>
                    <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h2>{editingTrainee ? 'Edit Trainee' : 'Add New Trainee'}</h2>
                            <button type="button" className={styles.modalClose} onClick={handleCloseModal} aria-label="Close">
                                <FiX />
                            </button>
                        </div>
                        <form onSubmit={(e) => {
                            e.preventDefault(); setError('');
                            const formData = {
                                name: e.target.traineeName.value,
                                email: e.target.traineeEmail.value,
                                startDate: e.target.startDate.value,
                                status: e.target.status.value,
                            };
                            const password = editingTrainee ? null : e.target.password?.value;
                            const confirmPassword = editingTrainee ? null : e.target.confirmPassword?.value;

                            if (!editingTrainee && password !== confirmPassword) { setError("Passwords do not match."); return; }
                            if (!editingTrainee && (!password || password.length < 6)) { setError("Password must be at least 6 characters long."); return; }
                            handleSaveTrainee(formData, password);
                        }}>
                            <div className={styles.formGroup}><label htmlFor="traineeName">Name:</label><input type="text" id="traineeName" name="traineeName" defaultValue={editingTrainee?.name || ''} required /></div>
                            <div className={styles.formGroup}><label htmlFor="traineeEmail">Email:</label><input type="email" id="traineeEmail" name="traineeEmail" defaultValue={editingTrainee?.email || ''} required disabled={!!editingTrainee && !!editingTrainee.authUid} />{editingTrainee && editingTrainee.authUid && <small>Email cannot be changed if an account is linked.</small>}</div>
                            {!editingTrainee && (<>
                                <div className={styles.formGroup}><label htmlFor="password">Password:</label><input type="password" id="password" name="password" required /></div>
                                <div className={styles.formGroup}><label htmlFor="confirmPassword">Confirm Password:</label><input type="password" id="confirmPassword" name="confirmPassword" required /></div>
                            </>)}
                            <div className={styles.formGroup}><label htmlFor="startDate">Start Date:</label><input type="date" id="startDate" name="startDate" defaultValue={editingTrainee?.startDate ? formatDate(editingTrainee.startDate, 'yyyy-MM-dd') : ''} /></div>
                            <div className={styles.formGroup}><label htmlFor="status">Status:</label><select id="status" name="status" defaultValue={editingTrainee?.status || 'Active'}><option value="Active">Active</option><option value="Completed">Completed</option><option value="Dropped">Dropped</option></select></div>
                            
                            <div className={styles.formGroup}>
                                <label>Supervisors:</label>
                                <div className={styles.checkboxGroupContainer}>
                                    {potentialSupervisors.length > 0 ? potentialSupervisors.map(supervisor => (
                                        <div key={supervisor.id} className={styles.checkboxItem}>
                                            <input
                                                type="checkbox"
                                                id={`supervisor-${supervisor.id}`}
                                                value={supervisor.id}
                                                checked={selectedSupervisorIds.includes(supervisor.id)}
                                                onChange={() => handleSupervisorSelectionChange(supervisor.id)}
                                                disabled={isProcessing}
                                            />
                                            <label htmlFor={`supervisor-${supervisor.id}`}>{supervisor.name}</label>
                                        </div>
                                    )) : <p>No potential supervisors found or list is loading.</p>}
                                </div>
                            </div>

                            {error && <p className={styles.errorMessage} style={{marginTop: '10px'}}>{error}</p>}
                            <div className={styles.modalActions}>
                                <button type="button" onClick={handleCloseModal} disabled={isProcessing} className={styles.cancelButton}>Cancel</button>
                                <button type="submit" disabled={isProcessing} className={styles.saveButton}>{isProcessing ? 'Saving...' : 'Save Trainee'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraineesPage;
